// Welche Routen die native App zeigt (Spec v3 §3.3a).
//
// Die App ist der Kundenbereich, keine verpackte Website (Apple Guideline 4.2).
// Alles, was hier nicht steht (Marketing, Blog, Landingpages, /admin, /walker, ...),
// wird in der App nicht gemountet, sondern auf /dashboard umgeleitet.
//
// Bewusst NICHT in der App (Entscheidung 01.10.2026):
// - /daycare/check (Wartelisten-Umfrage per Mail-Link, Empfänger meist noch keine Kunden)
// - /daycare/getemployee (QR-Abholen an der HuTa; in der App wird das ein Button im HuTa-Pop-up)
// - /impfpass/hochladen?t=… (persönlicher Upload-Link ohne Login, 20.10.2026): Web-Seite wie
//   /daycare/check. Aus der App heraus öffnet sie der In-App-Browser; angemeldete Kunden laden in
//   der App über /dashboard?impfpass=1 hoch. Keine Rechtsseiten-Titelzeile nötig, weil die Seite
//   nie in der App gerendert wird (und Universal Links sie ausschließen).

export const NATIVE_HOME = "/dashboard";

const EXAKT = new Set<string>([
  // Kern
  "/dashboard",
  "/buchen",
  "/booking",
  "/booking/success",
  "/payment-success",
  "/gassi-success",
  // Auth
  "/auth",
  "/login",
  "/auth/activate",
  // HuTa
  "/huta-gesundheit",
  "/daycare/onboarding",
  "/daycare/abo-absagen",
  "/kundenausweis",
  "/huta-telefon-termin",
  // Rechtliches (in der App Pflicht)
  "/impressum",
  "/agb",
  "/agb-hundetagesbetreuung",
  "/datenschutz",
  "/konto-loeschen",
  "/widerruf",
  "/vertraege-kuendigen",
]);

// Haushalt (KA-29): Einladungslinks /einladung/<token>.
// Kundenbereich v2 (K1): Tabs als eigene Adressen /dashboard/heute, /dashboard/hund/<id>, …
const PRAEFIXE = ["/dog/", "/einladung/", "/dashboard/"];

// Team-Seiten in der App (OA-5, Scope team-seiten-in-der-app 09.10.2026, Baustein B1).
// App-Freigabeliste: nur diese Admin-Adressen dürfen überhaupt in die App. Sichtbar ist die
// Schnittmenge aus dieser Liste und page_access der Person (canViewPath, src/lib/adminAccess.ts),
// ausgewertet im nachgeladenen Team-Zugang (src/components/team/TeamZugang.tsx). Neue Admin-Seiten
// bleiben in der App zu, bis sie bewusst hier eingetragen werden (nach Prüfung auf 390 px, B4).
// Bewusst NICHT: Brain (/admin), Kontrollzentrum, Personen & Rechte, Stats, Funnels, Planner,
// Walker-Seiten (/admin/dogwalker, /walker). Reihenfolge = Priorität für den Team-Start.
export const APP_TEAM_PFADE: readonly string[] = [
  // HuTa (group.daycare)
  "/admin/daycare/checkin",
  "/admin/daycare/gruppen",
  "/admin/daycare/todos",
  "/admin/daycare/aufgaben",
  "/admin/daycare/postfach",
  "/admin/daycare",
  // Gassi-Ops (group.gassi ohne /walker, group.operations nur Customer Support)
  "/admin/regeltermin",
  "/admin/walk-photo-audit",
  "/admin/customer-support",
];

function normalisieren(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

// Rechtsseiten in der App (Bug 16.10.2026: Impressum war eine Sackgasse). Die Seiten sind die
// Website-Seiten; in der App zeigt NativeRouteGuard darüber eine Titelzeile mit Zurück-Knopf
// (AppRechtsKopf), weil Wischen-zurück erst mit einem neuen Store-Build kommt.
// eigeneKopfzeile: Die Seite rendert <Navbar /> (in der App nur der Safe-Area-Platzhalter, der
// feste Abstand pt-24 der Seite hält die Titelzeile frei). Ohne: die Titelzeile bringt einen
// eigenen Platzhalter mit.
export const APP_RECHTS_SEITEN: Readonly<Record<string, { titel: string; eigeneKopfzeile: boolean }>> = {
  "/impressum": { titel: "Impressum", eigeneKopfzeile: true },
  "/datenschutz": { titel: "Datenschutz", eigeneKopfzeile: true },
  "/agb": { titel: "AGB", eigeneKopfzeile: true },
  "/konto-loeschen": { titel: "Konto löschen", eigeneKopfzeile: true },
  "/agb-hundetagesbetreuung": { titel: "AGB Hundetagesbetreuung", eigeneKopfzeile: false },
  "/widerruf": { titel: "Widerrufsbelehrung", eigeneKopfzeile: false },
  "/vertraege-kuendigen": { titel: "Verträge kündigen", eigeneKopfzeile: false },
};

/** Zurück-Ziel, wenn die Rechtsseite der erste Eintrag ist (Kaltstart, Link aus Mail). */
export const APP_RECHTS_ZURUECK = "/dashboard/ich/rechtliches";

/** Rechtsseite mit App-Titelzeile? (nur Pfad, gilt nur in der App) */
export function appRechtsSeite(pathname: string): { titel: string; eigeneKopfzeile: boolean } | null {
  return APP_RECHTS_SEITEN[normalisieren(pathname)] ?? null;
}

/** Steht die Adresse auf der App-Freigabeliste für Team-Seiten (ohne Rechte-Prüfung)? */
export function istAppTeamPfad(pathname: string): boolean {
  return APP_TEAM_PFADE.includes(normalisieren(pathname));
}

/**
 * Darf die App diese Adresse zeigen?
 * Ohne teamPfade (Kunde, ausgeloggt, Schalter aus) exakt wie bisher. Mit teamPfade (die für diese
 * Person freigegebenen Team-Adressen, schon geschnitten mit page_access) zusätzlich genau diese.
 */
export function isAllowedNativePath(pathname: string, teamPfade?: ReadonlySet<string>): boolean {
  const p = normalisieren(pathname);
  if (EXAKT.has(p) || PRAEFIXE.some((prefix) => p.startsWith(prefix))) return true;
  return !!teamPfade && teamPfade.has(p);
}
